import { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Trophy,
  Activity,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';

const AnalyticsDashboard = () => {
  const {
    userAnalytics,
    challengeAnalytics,
    activityAnalytics,
    fetchUserAnalytics,
    fetchChallengeAnalytics,
    fetchActivityAnalytics,
  } = useAdminStore();

  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUserAnalytics(period),
        fetchChallengeAnalytics(),
        fetchActivityAnalytics(period),
      ]);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [period]);

  const ProgressBar = ({ value, max, color = 'bg-blue-500' }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full ${color}`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h2>
          <p className="text-gray-500">Platform performance and user insights</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* User Analytics */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-800">User Analytics</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {userAnalytics?.total_users || 0}
              </div>
              <div className="text-sm text-gray-500">Total Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {userAnalytics?.new_users || 0}
              </div>
              <div className="text-sm text-gray-500">New Users ({period})</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {userAnalytics?.active_users || 0}
              </div>
              <div className="text-sm text-gray-500">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {Math.round((userAnalytics?.verified_rate || 0) * 100)}%
              </div>
              <div className="text-sm text-gray-500">Verification Rate</div>
            </div>
          </div>

          {/* User Growth Chart Placeholder */}
          <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-200">
            <div className="text-center text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>User growth trend over {period}</p>
              <p className="text-sm">(Chart visualization coming soon)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge Analytics */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-800">Challenge Performance</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {challengeAnalytics?.total_attempts || 0}
              </div>
              <div className="text-sm text-gray-500">Total Attempts</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {challengeAnalytics?.completions || 0}
              </div>
              <div className="text-sm text-gray-500">Completions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {Math.round(challengeAnalytics?.avg_score || 0)}
              </div>
              <div className="text-sm text-gray-500">Average Score</div>
            </div>
          </div>

          {/* Top Challenges */}
          <h4 className="font-medium text-gray-700 mb-4">Most Popular Challenges</h4>
          <div className="space-y-4">
            {(challengeAnalytics?.popular_challenges || []).slice(0, 5).map((challenge, index) => (
              <div key={challenge.id || index} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center font-bold text-yellow-700">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{challenge.title}</span>
                    <span className="text-sm text-gray-500">{challenge.attempts} attempts</span>
                  </div>
                  <ProgressBar
                    value={challenge.attempts}
                    max={challengeAnalytics?.popular_challenges?.[0]?.attempts || 100}
                    color="bg-yellow-500"
                  />
                </div>
              </div>
            ))}
            {(!challengeAnalytics?.popular_challenges || challengeAnalytics.popular_challenges.length === 0) && (
              <p className="text-center text-gray-500 py-4">No challenge data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Activity Analytics */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <Activity className="w-5 h-5 text-green-500" />
          <h3 className="font-semibold text-gray-800">Platform Activity</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {activityAnalytics?.total_submissions || 0}
              </div>
              <div className="text-sm text-gray-500">Total Submissions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {activityAnalytics?.daily_avg || 0}
              </div>
              <div className="text-sm text-gray-500">Daily Average</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {(activityAnalytics?.total_dp_earned || 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">DP Earned</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {activityAnalytics?.components_unlocked || 0}
              </div>
              <div className="text-sm text-gray-500">Components Unlocked</div>
            </div>
          </div>

          {/* Daily Activity */}
          <h4 className="font-medium text-gray-700 mb-4">Daily Activity ({period})</h4>
          <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-200">
            <div className="text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Daily activity breakdown</p>
              <p className="text-sm">(Chart visualization coming soon)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Growth Metrics
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">User Growth Rate</span>
              <span className="font-medium text-green-600">
                +{userAnalytics?.growth_rate || 0}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Engagement Rate</span>
              <span className="font-medium text-blue-600">
                {Math.round((activityAnalytics?.engagement_rate || 0) * 100)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Completion Rate</span>
              <span className="font-medium text-purple-600">
                {Math.round((challengeAnalytics?.completion_rate || 0) * 100)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Avg. Session Duration</span>
              <span className="font-medium text-orange-600">
                {activityAnalytics?.avg_session || '~15 min'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Platform Health
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">API Uptime</span>
              <span className="font-medium text-green-600">99.9%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Avg. Response Time</span>
              <span className="font-medium text-blue-600">~120ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Error Rate</span>
              <span className="font-medium text-green-600">&lt;0.1%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Database Status</span>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-sm rounded-full">
                Healthy
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
